import { action, observable } from 'mobx';
import { container, singleton } from 'tsyringe';

import { assertPresence, errorHandling } from '../../../utils/error-handling';
import type { AppStore } from '../../app/app-store';
import { DmsStore } from '../../dms';
import { RailTypes } from '../../entities';
import { ErrorPriority } from '../../error';
import { ProvidersRepository } from '../../providersRepository';
import { GridPage } from '../aside';

import { createGridRail } from './grid-page-helper';

@singleton()
export class GridPageStoreV2 {
  static moduleName = 'GridPageStoreV2';

  @observable isReady = false;
  @observable railStore?: RailTypes;
  @observable providerLogo?: string;
  private gridPage?: GridPage;
  private lastTargetGridPointer?: string;
  private lastTargetId?: string | number;

  private get appStore() {
    return container.resolve<AppStore>('appStore');
  }
  /**
   * Initializes grid store with data rail underneath
   * @param targetGridPointer page referenceId
   * @param targetId reference rail id or collection id
   */
  @action
  @errorHandling({ type: ErrorPriority.high })
  async init(targetGridPointer: string, targetId: string | number): Promise<void> {
    if (targetGridPointer === this.lastTargetGridPointer && targetId === this.lastTargetId) {
      this.isReady = true;
      return;
    }
    this.resetStore();

    this.lastTargetGridPointer = targetGridPointer;
    this.lastTargetId = targetId;

    this.gridPage = this.appStore.rootStore?.grid?.getByReferenceId(targetGridPointer);

    const dmsStore = container.resolve<DmsStore>('dmsStore');

    if (this.gridPage) {
      this.railStore = await createGridRail(
        this.gridPage,
        assertPresence(this.appStore.rootStore).children,
        targetId,
        dmsStore.dms,
      );
    }

    const providersRepository = container.resolve<ProvidersRepository>('providersRepository');
    this.providerLogo = providersRepository.providerGridLogo(this.gridPage?.name);

    await this.railStore?.init();

    this.isReady = true;
  }

  @action
  onInvisible(): void {
    this.isReady = false;
  }

  @action
  resetStore() {
    this.isReady = false;
    this.gridPage = undefined;
    this.railStore && this.railStore.resetStore();
    this.railStore = undefined;
  }
}
